/**
 * Add / remove Constraints on a Solution (`POST` / `DELETE
 * /api/dashboard/solutions/:id/constraints[/:constraintId]`, src/api/solution-constraints.ts).
 *
 * Add is idempotent (a repeat tick is a no-op), remove is a 404 once the row is gone, and
 * every real change recomputes + snapshots the Fit Score after it commits — so a success
 * here means the Solution Detail already carries the new score. The hooks own their
 * invalidation and their toasts (frontend rule), and because those live on the mutation —
 * not on a `mutate()` call-site callback — they still run if the drawer closes mid-flight.
 * Invalidation runs on settle, not only on success: after a 404 or a 409 the drawer should
 * show the server's truth, not the chip that just failed.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import {
  atlasSuggestKeys,
  dashboardHeatmapKeys,
  solutionDashboardKeys,
  solutionDetailKeys,
} from "@/lib/query";

/** One Constraint on a Solution, as the Solution Detail payload carries it. */
export interface SolutionConstraintItem {
  id: string;
  name: string;
  group: string;
  /** true = copied from the Account at Solution creation (§4.3 Rule 10); false = added here. */
  inherited: boolean;
}

/**
 * What a mutation needs beyond the id: the name for the toast, and — on remove —
 * whether the row was inherited and from which Account, because "Removed Healthcare
 * (inherited from Acme)" is the one fact a reader wants once an inherited row is gone.
 */
export interface SolutionConstraintChange {
  constraintId: string;
  name: string;
  inherited: boolean;
  accountName: string;
}

/**
 * Everything a Constraint change can move: the drawer (score, list), the Solutions
 * dashboard (Fit tier), the Resources heatmap, and the Atlas candidates — whose
 * `n/total constraints` coverage is computed against this list.
 */
function useInvalidateSolutionConstraints(solutionId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: solutionDetailKeys.detail(solutionId) });
    qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
    qc.invalidateQueries({ queryKey: dashboardHeatmapKeys.all });
    qc.invalidateQueries({ queryKey: atlasSuggestKeys.forSolution(solutionId) });
  };
}

export function useAddSolutionConstraint(solutionId: string) {
  const invalidate = useInvalidateSolutionConstraints(solutionId);
  return useMutation({
    // One scope for add and remove: TanStack runs same-scope mutations one at a time,
    // across both hooks. The picker lets several ticks fly, and each recomputes the
    // Fit Score server-side with no lock — interleaved, a stale snapshot could land
    // last and read as "latest" (Appendix B #4). Each call keeps its own pending state.
    scope: { id: `solution-constraints:${solutionId}` },
    mutationFn: ({ constraintId }: SolutionConstraintChange) =>
      jsonFetch<{ ok: boolean }>(`/api/dashboard/solutions/${solutionId}/constraints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ constraintId }),
      }),
    onSuccess: (_result, { name }) => toast.success(`Added ${name}`),
    onSettled: invalidate,
    onError: (e: Error, { name }) =>
      toast.error(`Couldn't add ${name}`, { description: e.message }),
  });
}

export function useRemoveSolutionConstraint(solutionId: string) {
  const invalidate = useInvalidateSolutionConstraints(solutionId);
  return useMutation({
    // Serialised with the add hook — see above.
    scope: { id: `solution-constraints:${solutionId}` },
    mutationFn: ({ constraintId }: SolutionConstraintChange) =>
      jsonFetch<{ ok: boolean }>(
        `/api/dashboard/solutions/${solutionId}/constraints/${constraintId}`,
        { method: "DELETE" },
      ),
    onSuccess: (_result, { name, inherited, accountName }) => {
      toast.success(
        inherited ? `Removed ${name} (inherited from ${accountName})` : `Removed ${name}`,
      );
    },
    onSettled: invalidate,
    onError: (e: Error, { name }) =>
      toast.error(`Couldn't remove ${name}`, { description: e.message }),
  });
}
